import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getExpiringProjects } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Loader2, RefreshCw, Calendar, DollarSign, Eye, ShoppingCart } from "lucide-react";

const PRODUCT_LABELS = {
  marketing: "Marketing", web_design: "Web Design", seo_aeo: "SEO/AEO",
  prospect_tools: "ProspectID/Max", digital_marketing: "Digital Marketing", ai_tools: "AI Tools",
};

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—");
const fmtMoney = (n) => `$${Number(n || 0).toLocaleString()}`;
const daysUntil = (iso) => {
  if (!iso) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(iso);
  return Math.ceil((target - today) / (1000 * 60 * 60 * 24));
};

export default function ExpiringRenewalsPage() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getExpiringProjects(30)
      .then(({ data }) => setProjects(data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const totalValue = projects.reduce((sum, p) => sum + (p.contract_value || 0), 0);

  const urgencyClass = (days) => {
    if (days <= 7) return "bg-red-100 text-red-800";
    if (days <= 14) return "bg-amber-100 text-amber-800";
    return "bg-yellow-100 text-yellow-800";
  };

  if (loading) {
    return <div className="flex items-center justify-center h-[60vh]"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="expiring-renewals-page">
      <div>
        <h1 className="font-outfit text-3xl font-bold tracking-tight">Renewal Opportunities</h1>
        <p className="text-muted-foreground mt-1">Contracts ending within the next 30 days — start the renewal conversation</p>
      </div>

      {/* Summary stats */}
      <div className="grid gap-4 sm:grid-cols-3">
        <Card className="border bg-amber-50/40">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center"><RefreshCw className="h-5 w-5 text-amber-700" /></div>
              <div>
                <p className="text-xs uppercase text-muted-foreground">Contracts Ending</p>
                <p className="text-2xl font-bold">{projects.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border bg-emerald-50/40">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-emerald-100 flex items-center justify-center"><DollarSign className="h-5 w-5 text-emerald-700" /></div>
              <div>
                <p className="text-xs uppercase text-muted-foreground">Total Contract Value</p>
                <p className="text-2xl font-bold">{fmtMoney(totalValue)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border bg-blue-50/40">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center"><Calendar className="h-5 w-5 text-blue-700" /></div>
              <div>
                <p className="text-xs uppercase text-muted-foreground">Window</p>
                <p className="text-2xl font-bold">Next 30 Days</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      {projects.length === 0 ? (
        <Card className="border"><CardContent className="flex flex-col items-center justify-center py-16">
          <RefreshCw className="h-16 w-16 text-muted-foreground/40 mb-4" />
          <h3 className="text-lg font-medium">No contracts ending soon</h3>
          <p className="text-muted-foreground text-sm">All clear for the next 30 days.</p>
        </CardContent></Card>
      ) : (
        <Card className="border">
          <CardHeader><CardTitle className="text-lg">Expiring Contracts</CardTitle></CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead>End Date</TableHead>
                  <TableHead>Days Left</TableHead>
                  <TableHead>Contract Value</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {projects.map((p) => {
                  const days = daysUntil(p.end_date);
                  const productLabel = (p.product_types || [p.product_type]).map((t) => PRODUCT_LABELS[t] || t).join(" + ");
                  return (
                    <TableRow key={p.id} data-testid={`renewal-row-${p.id}`}>
                      <TableCell className="font-medium">{p.client_name}</TableCell>
                      <TableCell>{productLabel}</TableCell>
                      <TableCell>{fmtDate(p.end_date)}</TableCell>
                      <TableCell>
                        <Badge className={urgencyClass(days)}>{days} {days === 1 ? "day" : "days"}</Badge>
                      </TableCell>
                      <TableCell>{p.contract_value ? fmtMoney(p.contract_value) : "—"}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <Link to={`/orders/new?client=${p.client_id}`}>
                            <Button variant="default" size="sm" data-testid={`renew-${p.id}`}>
                              <ShoppingCart className="mr-1.5 h-3.5 w-3.5" /> Renew / Re-order
                            </Button>
                          </Link>
                          <Link to={`/orders/${p.id}`}>
                            <Button variant="ghost" size="sm">
                              <Eye className="mr-1.5 h-3.5 w-3.5" /> View
                            </Button>
                          </Link>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
